import { Capacitor } from '@capacitor/core'
import { ApiService } from '~/services/api.service'

export interface GmailConsentRequirements {
  consentVersion: string
  privacyPolicyUrl: string
  termsUrl: string
}

// Placeholders until the backend provides real URLs (privacy_policy_url/terms_url are
// currently null in the consent-requirements response).
export const GMAIL_CONSENT_DEFAULT_PRIVACY_POLICY_URL = '/privacy-policy/'
export const GMAIL_CONSENT_DEFAULT_TERMS_URL = '/terms-of-use/'

const GENERIC_ERROR = 'Something went wrong. Please try again.'
const STALE_VERSION_ERROR = 'Your consent details were out of date — please try again.'

// The backend's error text: its `message`, else the first field error (e.g.
// { errors: { email: ['…'] } }), else '' so the caller falls back to its own copy.
const errorMessageFrom = (body: any): string => {
  if (typeof body?.message === 'string' && body.message.trim()) return body.message
  const firstFieldErrors = body?.errors && Object.values(body.errors)[0]
  const first = Array.isArray(firstFieldErrors) ? firstFieldErrors[0] : firstFieldErrors
  return typeof first === 'string' ? first : ''
}

/*
 * Backs the Gmail-connect consent screen: fetches what the consent screen needs to render
 * (consent_version, privacy/terms links) and kicks off the OAuth redirect once the member
 * agrees. Mirrors useEnergyCdrConnection's shape (composable-local state, no Pinia) since this
 * is the closest existing precedent for an external-consent + redirect flow in this app.
 */
export const useGmailConsent = () => {
  const requirements = ref<GmailConsentRequirements | null>(null)
  const isLoadingRequirements = ref(false)
  const isConnecting = ref(false)
  const error = ref('')
  const memberhubApi = new ApiService()

  const fetchConsentRequirements = async (): Promise<GmailConsentRequirements | null> => {
    isLoadingRequirements.value = true
    error.value = ''

    try {
      const response = await memberhubApi.get('/gmail/consent-requirements')
      if (response?.error) {
        error.value = GENERIC_ERROR
        return null
      }

      const data = response?.data?.data || {}
      requirements.value = {
        consentVersion: data.consent_version,
        privacyPolicyUrl: data.privacy_policy_url || GMAIL_CONSENT_DEFAULT_PRIVACY_POLICY_URL,
        termsUrl: data.terms_url || GMAIL_CONSENT_DEFAULT_TERMS_URL
      }
      return requirements.value
    } catch {
      error.value = GENERIC_ERROR
      return null
    } finally {
      isLoadingRequirements.value = false
    }
  }

  /*
   * Sends the consent version and the address the member typed to POST /gmail/connect.
   * The backend validates the email and embeds it in the Google URL as login_hint, so the
   * returned redirect_url is used exactly as-is. Returns it on success, or null on failure
   * (error is set for the caller to render). The backend's own message is shown when it
   * sends one; otherwise a 422 means the consent_version is missing/stale, so this
   * re-fetches requirements so a retry uses the up-to-date version.
   */
  const connectGmail = async (consentVersion: string, email: string): Promise<string | null> => {
    isConnecting.value = true
    error.value = ''

    try {
      // credentials: 'include' lets the browser keep the gmail_oauth_browser cookie
      // the backend sets here: the callback checks it against the OAuth state, so a
      // connect link can't be completed in someone else's browser. Only this call
      // needs a cookie; everything else uses the Bearer token.
      // In the native app, origin (ios|android) tells the backend the connect comes
      // from the app; the web sends none.
      const response = await memberhubApi.post('/gmail/connect', {
        consent_version: consentVersion,
        email: String(email || '').trim(),
        ...(Capacitor.isNativePlatform() ? { origin: Capacitor.getPlatform() } : {})
      }, { credentials: 'include' })

      if (response?.error) {
        const backendMessage = errorMessageFrom(response.data)
        if (response.code === 422) {
          await fetchConsentRequirements()
          error.value = backendMessage || STALE_VERSION_ERROR
        } else {
          error.value = backendMessage || GENERIC_ERROR
        }
        return null
      }

      return response?.data?.data?.redirect_url || null
    } catch {
      error.value = GENERIC_ERROR
      return null
    } finally {
      isConnecting.value = false
    }
  }

  /*
   * Native app only (CLUB-480). The backend's browser-binding cookie can't reach
   * the system browser where Google runs, so for a connect sent with an `origin`
   * the callback returns to the app (compareclub(-dev)://email-connect/callback)
   * with a single-use completion code instead of linking the account. This
   * finishes the connect as the signed-in member (Bearer only, no cookie).
   * Any failure, including an expired, used or someone else's code, is 'error'.
   */
  const completeGmailConnect = async (completionCode: string): Promise<{ gmailStatus: string, accountId: string }> => {
    const failed = { gmailStatus: 'error', accountId: '' }
    try {
      const response = await memberhubApi.post('/gmail/connect/complete', { completion_code: completionCode })
      const result = response?.data?.data
      if (response?.error || !result?.gmail_status) return failed
      return {
        gmailStatus: String(result.gmail_status),
        accountId: result.account_id != null ? String(result.account_id) : ''
      }
    } catch {
      return failed
    }
  }

  return {
    requirements,
    isLoadingRequirements,
    isConnecting,
    error,
    fetchConsentRequirements,
    connectGmail,
    completeGmailConnect
  }
}
